import DatetimeMixin from '@/mixins/datetime-mixin';
import { ROLE_HISTORY_LOGS } from '@/sections/roles/roles-mutation-types';
import _ from 'lodash';

export default {
	mixins: [DatetimeMixin],
	computed: {
		getCharacterLimit() {
			const MAX_WIDTH_ALLOW = this.pixelToCharacterLen(
				this.actionClientWidth
			);

			const limit = MAX_WIDTH_ALLOW;

			return limit;
		}
	},
	updated() {
		this.$nextTick(() => {
			if (this.$refs.actionWidthRef) {
				const newWidth = this.$refs.actionWidthRef.clientWidth;

				if (
					!this.actionClientWidth &&
					newWidth !== this.actionClientWidth
				) {
					this.refreshSource(newWidth);
				}
			}
		});
	},
	mounted() {
		window.addEventListener('resize', this.handleWindowSizeChange);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.handleWindowSizeChange);
	},
	methods: {
		handleWindowSizeChange() {
			this.windowWidth = window.innerWidth;
		},
		refreshSource(newWidth) {
			this.actionClientWidth = newWidth;

			const roleHistoryLogs = _.cloneDeep(this[ROLE_HISTORY_LOGS]);

			if (!_.isEmpty(roleHistoryLogs)) {
				this.tableSource.source = this.parseRolesHistoryLogs(
					roleHistoryLogs,
					false
				);
			}
		},
		parseRolesHistoryLogs(response, isCSV) {
			const parsedResponse = this.parseRoleLogs(response, isCSV);
			return parsedResponse;
		},
		parseRoleLogs(response, isCSV) {
			let resp = _.cloneDeep(response);
			resp.items = response.items.map(element =>
				this.createLogItem(element)
			);
			resp.items.forEach(item => {
				this.formatRoleActionText(item, isCSV);
			});

			return resp;
		},
		createLogItem(element) {
			let item = {
				action: element.action,
				timestamp: this.fullDateString(element.timestamp, true),
				differenceDateString: this.differenceDateString(
					element.timestamp
				),
				changedByUserName:
					element.changedByUserName ||
					`User Id ${element.changedByUserId}`,
				data: element.data,
				fieldChanges: element.fieldChanges?.items.filter(
					i => i.ChangedPropertyName != 'RoleDescription'
				)
			};

			if (this.actionClientWidth > 0) {
				switch (element.action) {
					case 'Add':
						item = this.formatAdd(item, element);
						break;

					case 'Update':
						item = this.formatUpdate(item, element);
						break;
				}
			}

			return item;
		},
		formatAdd(item, element) {
			const {
				ClientId: clientId,
				Customers: customers,
				Permissions: newPermissions,
				Role: roleName,
				Active: active
			} = element.data.items[1];
			const permissionsFormat = this.findPermissionsGroupNames(
				newPermissions
			);

			Object.assign(item, {
				changedAction: 'Add',
				changedPropertyName:
					clientId === 'vue-support-external'
						? 'Customers'
						: 'Permissions',
				roleName,
				permissions: { added: permissionsFormat, removed: [] },
				customers: { added: customers, removed: [] },
				active
			});
			return item;
		},
		formatUpdate(item, element) {
			const STATUS = 'Active';
			const changes = element.fieldChanges?.items.filter(
				x => x.ChangedPropertyName != 'RoleDescription'
			);

			if (!changes) {
				return this.handleCreateRole(item, element);
			}

			const {
				changedPropertyNames,
				isOnePropertyName
			} = this.getChangedPropertyNames(changes);

			if (isOnePropertyName) {
				return this.handleSinglePropertyChange(
					item,
					changes,
					changedPropertyNames
				);
			} else {
				return this.handleMultiplePropertyChanges(
					item,
					changes,
					changedPropertyNames,
					STATUS
				);
			}
		},
		formatUpdateStatusRole(item, changes) {
			let { ChangedPropertyName, NewValue, OriginalValue } = changes;

			Object.assign(item, {
				changedPropertyName: ChangedPropertyName,
				changedAction: 'Update',
				statusRole: {
					statusAction: 'Update',
					newStatusValue: NewValue,
					originalStatusValue: OriginalValue
				},
				permissions: [],
				customers: []
			});
			return item;
		},
		formatUpdateRoles(item, changes, changedPropertyName) {
			const getItems = (action, changes, propertyName) => {
				let results = changes
					.filter(
						c =>
							c.Action === action &&
							c.ChangedPropertyName.includes(propertyName)
					)
					.map(p => {
						return action === 'Add' ? p.NewValue : p.OriginalValue;
					});
				if (propertyName === 'Permissions') {
					results = this.findPermissionsGroupNames(results);
				}

				return results;
			};

			let removed = getItems('Delete', changes, changedPropertyName);
			let added = getItems('Add', changes, changedPropertyName);

			Object.assign(item, {
				changedPropertyName: changedPropertyName,
				changedAction: 'Update',
				removed,
				added
			});
			return item;
		},
		getChangedPropertyNames(changes) {
			const changedPropertyNames = changes.map(change =>
				this.removeIdPropName(change.ChangedPropertyName)
			);
			const uniqueChangedPropertyNames = this.uniqueValues(
				changedPropertyNames
			);
			const isOnePropertyName = uniqueChangedPropertyNames.length === 1;

			return {
				changedPropertyNames: isOnePropertyName
					? uniqueChangedPropertyNames[0]
					: uniqueChangedPropertyNames,
				isOnePropertyName
			};
		},
		handleCreateRole(item, element) {
			if (element.data[0] === null && element.data[1]) {
				return this.formatAdd(item, element);
			}
			return item;
		},
		handleSinglePropertyChange(item, changes, changedPropertyNames) {
			const STATUS = 'Active';
			if (changedPropertyNames === STATUS && changes.length === 1) {
				return this.formatUpdateStatusRole(item, changes[0]);
			}

			let data = this.formatUpdateRoles(
				item,
				changes,
				changedPropertyNames
			);

			let resultData = {
				...data
			};
			if (changedPropertyNames === 'Permissions') {
				resultData.permissions = {
					added: data.added,
					removed: data.removed
				};
			} else if (changedPropertyNames === 'Customers') {
				resultData.customers = {
					added: data.added,
					removed: data.removed
				};
			}
			return resultData;
		},
		handleMultiplePropertyChanges(
			item,
			changes,
			changedPropertyNames,
			STATUS
		) {
			const { items } = item.data;
			const clientId = items[0]?.ClientId || items[1]?.ClientId;
			const generalPropertyName =
				clientId === 'vue-support-external'
					? 'Customers'
					: 'Permissions';

			let newItem = {};

			changedPropertyNames.forEach(propertyName => {
				if (propertyName !== STATUS) {
					let data = this.formatUpdateRoles(
						item,
						changes,
						propertyName
					);
					newItem = {
						...newItem,
						...data,
						changedPropertyName: generalPropertyName
					};

					if (propertyName === 'Permissions') {
						newItem.permissions = {
							added: data.added,
							removed: data.removed
						};
					}
					if (propertyName === 'Customers') {
						newItem.customers = {
							added: data.added,
							removed: data.removed
						};
					}
				} else {
					const objStatus = changes.find(
						change => change.ChangedPropertyName === STATUS
					);
					const { NewValue, OriginalValue } = objStatus;
					newItem.statusRole = {
						statusAction: 'Update',
						newStatusValue: NewValue,
						originalStatusValue: OriginalValue
					};
				}
			});
			return newItem;
		},
		formatRoleActionText(item, isCSV) {
			if (!item) return '';
			item = this.handleActions(item, isCSV);

			return isCSV ? item.action : item.actionDescription;
		},
		handleActions(item, isCSV) {
			const tags = {
				bTag: isCSV ? '' : '<strong>',
				eTag: isCSV ? '' : '</strong>'
			};
			const validate = isCSV ? true : this.actionClientWidth > 0;
			const { changedAction, fieldChanges } = item;

			let results = [];
			let actionsToDisplayHTML = '';

			if (!validate) return item;

			switch (changedAction) {
				case 'Add':
					item = this.pushCreatedRole(item, results, tags);
					item = this.handleUpdateAction(
						item,
						results,
						actionsToDisplayHTML,
						tags
					);
					break;

				case 'Update':
					if (!fieldChanges) return item;
					item = this.handleUpdateAction(
						item,
						results,
						actionsToDisplayHTML,
						tags
					);
					break;
			}
			if (this.downloadingCSV) {
				item.action = results.join(' ');
			}
			return this.updateActionDescription(item, results, isCSV);
		},
		sumTotalChanges(item) {
			const countRoleCreated = 1;

			if (item.hasTruncate) {
				item.totalChanges = item.statusRole
					? item.totalChanges + 1
					: item.totalChanges;

				if (item.action == 'Add') {
					item.totalChanges = item.totalChanges + countRoleCreated;
				}

				item = this.setTotalChangesDescription(item);
			}
			return item;
		},
		handleUpdateAction(item, results, actionsToDisplayHTML, tags) {
			const PERMISSIONS = 'Permissions';
			const CUSTOMERS = 'Customers';
			const actionMap = {
				Permissions: this.handleUpdatePermissionAction,
				Customers: this.handleUpdateCustomersAction
			};

			let newItem = {};

			if (!_.isEmpty(item.permissions)) {
				let { added, removed } = item.permissions;
				Object.assign(newItem, { added, removed });

				const actionHandler = actionMap[PERMISSIONS];

				newItem = actionHandler.call(
					this,
					newItem,
					results,
					actionsToDisplayHTML,
					tags
				);
				Object.assign(item, {
					hasTruncate: newItem.hasTruncate,
					totalChanges: newItem.totalChanges
				});
			}

			if (!_.isEmpty(item.customers)) {
				let { added, removed } = item.customers;
				Object.assign(newItem, { added, removed });

				if (!newItem.hasTruncate) {
					const actionHandler = actionMap[CUSTOMERS];
					newItem = actionHandler.call(
						this,
						newItem,
						results,
						actionsToDisplayHTML,
						tags
					);
					Object.assign(item, {
						hasTruncate: newItem.hasTruncate,
						totalChanges: newItem.totalChanges
					});
				} else {
					const totalCustomers = added.length + removed.length;
					Object.assign(item, {
						hasTruncate: newItem.hasTruncate,
						totalChanges: newItem.totalChanges + totalCustomers
					});
				}
			}

			if (!newItem.hasTruncate && item.statusRole) {
				item = this.pushStatusRole(item, results, tags);
			}

			item = this.sumTotalChanges(item);

			return item;
		},
		removeIdPropName(obj) {
			return obj.replace(/\[[^\]]*\]/g, '');
		},
		uniqueValues(array) {
			let map = new Map();
			array.forEach(value => map.set(value, value));
			const result = [...map.values()];
			return result;
		},
		pushCreatedRole(item, results, tags) {
			const { eTag, bTag } = tags;
			const itemsToAdd = [
				'Role',
				`${bTag}${item.roleName}${eTag}`,
				'created,'
			];

			if (!item.results) {
				this.$set(item, 'results', results);
			}

			item.results.push(...itemsToAdd);

			item = this.truncateActions(item);
			return item;
		},
		pushPermissions(item) {
			const { tags, permission, action } = item;
			const { permissionNameFormatted, access } = permission;
			const { eTag, bTag } = tags;

			const itemsToAdd = [
				`${bTag}${permissionNameFormatted}${eTag}`,
				action,
				access.split(',').length === 4 ? 'All' : access,
				'access,'
			];
			if (!item.results) {
				this.$set(item, 'results', []);
			}

			item.results.push(...itemsToAdd);
			item = this.truncateActions(item);

			return item;
		},
		pushCustomers(item) {
			const { tags, customers, action } = item;
			const { CustomerName } = customers;
			const { eTag, bTag } = tags;
			const itemsToAdd = [`${bTag}${CustomerName}${eTag}`, `${action}, `];

			if (!item.results) {
				this.$set(item, 'results', []);
			}

			item.results.push(...itemsToAdd);
			item = this.truncateActions(item);

			return item;
		},
		pushStatusRole(item, results, tags) {
			const { bTag, eTag } = tags;
			const currentStatusValue = item.statusRole
				? item.statusRole.newStatusValue
				: item.newStatusValue;

			const statusValue =
				currentStatusValue === true ? 'Active' : 'Inactive';

			const itemsToAdd = [
				`${bTag}Role Status ${eTag}`,
				`set to ${statusValue} ,`
			];

			if (!item.results) {
				this.$set(item, 'results', results);
			}

			item.results.push(...itemsToAdd);

			item = this.truncateActions(item);
			return item;
		},
		truncateActions(item) {
			const actions = [...item.results];

			const actionsText = this.convertArrayToStringAndRemoveHTML(actions);
			const limit = this.getCharacterLimit;
			this.$set(item, 'hasTruncate', false);
			item.actionsToDisplayHTML = actionsText;

			const applyTruncation = actionsText.length > limit;

			if (applyTruncation) {
				this.$set(item, 'hasTruncate', true);
				item.actionsToDisplayHTML = actionsText.slice(0, limit);
			}

			return item;
		},
		convertArrayToStringAndRemoveHTML(array) {
			return array.join(' ').replace(/(<([^>]+)>)/gi, '');
		},
		updateActionDescription(item, results, isCSV) {
			const updatedResults = results.join(' ').trim();
			let actionDescription;

			if (item.hasTruncate && !isCSV) {
				actionDescription = `${updatedResults} ...`;
			} else {
				actionDescription = updatedResults.endsWith(',')
					? updatedResults.slice(0, -1)
					: updatedResults;
			}

			this.$set(item, 'actionDescription', actionDescription);
			return item;
		},
		setTotalChangesDescription(item) {
			const total = item.totalChanges;

			item.totalChangeDescription = total
				? `+${total} more changes`
				: `+1 more changes`;
			return item;
		},

		handleUpdatePermissionAction(
			item,
			results,
			actionsToDisplayHTML,
			tags
		) {
			return this.handleUpdateChangeProperty(
				item,
				results,
				tags,
				({ action, results, item, actionsToDisplayHTML, tags }) =>
					this.pushPermissions({
						action,
						results,
						permission: item,
						actionsToDisplayHTML,
						tags
					}),
				(results, actions, action, actionsToDisplayHTML) =>
					this.computeTotalRemainingLogs({
						results,
						actions,
						action,
						delimiter: 'access',
						actionsToDisplayHTML
					})
			);
		},
		handleUpdateCustomersAction(item, results, actionsToDisplayHTML, tags) {
			return this.handleUpdateChangeProperty(
				item,
				results,
				tags,
				({ action, results, item, actionsToDisplayHTML, tags }) =>
					this.pushCustomers({
						action,
						results,
						customers: item,
						actionsToDisplayHTML,
						tags
					}),
				(results, actions, action, actionsToDisplayHTML) =>
					this.computeTotalRemainingLogs({
						results,
						actions,
						action,
						delimiter: ', ',
						actionsToDisplayHTML
					})
			);
		},
		handleUpdateChangeProperty(
			item,
			results,
			tags,
			fnInsertItems,
			computeTotalRemaining
		) {
			let isItemTruncated = false;
			let totalMoreChangesAdded = 0;
			let totalMoreChangesRemoved = 0;
			let actionsToDisplay;

			const processItems = (actions, action) => {
				actions.forEach(itm => {
					let data = fnInsertItems({
						action,
						results,
						item: itm,
						tags
					});
					if (!this.downloadingCSV) {
						isItemTruncated = data.hasTruncate || false;
						actionsToDisplay = data.actionsToDisplayHTML;
					}
				});
				return computeTotalRemaining(
					results,
					actions,
					action,
					actionsToDisplay
				);
			};
			if (item.added?.length > 0) {
				totalMoreChangesAdded = processItems(item.added, 'added');
			}

			if (item.removed?.length > 0) {
				totalMoreChangesRemoved = processItems(item.removed, 'removed');
			}

			if (isItemTruncated) {
				item.hasTruncate = isItemTruncated;
				item.totalChanges =
					totalMoreChangesAdded + totalMoreChangesRemoved;
			}

			return item;
		},
		findWord(sentence, specificWord) {
			let words = sentence.split(' '); // Split the string into an array of words
			let lastWord = words[words.length - 1]; // Access the last word
			return specificWord === lastWord;
		},
		computeTotalRemainingLogs(value) {
			const { actions, action, delimiter, actionsToDisplayHTML } = value;
			if (actionsToDisplayHTML) {
				const hasDelimiterInLastWord = this.findWord(
					actionsToDisplayHTML,
					delimiter
				);
				let displayedActions = actionsToDisplayHTML.split(delimiter);
				let finalDisplayedActions = displayedActions;

				if (!hasDelimiterInLastWord) {
					finalDisplayedActions = displayedActions.slice(0, -1);
				}

				const totalDisplayedActions = finalDisplayedActions.reduce(
					(count, element) => {
						if (element.includes(action)) {
							count++;
						}
						return count;
					},
					0
				);

				return actions.length - totalDisplayedActions;
			} else {
				return 0;
			}
		},
		findPermissionsGroupNames(modifiedArray) {
			let permissionsgroupNames = this.findPermissionsByList(
				modifiedArray
			);

			permissionsgroupNames = this.groupByGroupName(
				permissionsgroupNames
			);
			return permissionsgroupNames;
		},
		findPermissionsByList(arr) {
			if (_.isEmpty(this.permissions.items)) return [];
			const allPermissions = [...this.permissions.items];
			let newArr = [];
			allPermissions.some(item => {
				arr.find(element => {
					for (let i in item.permissions) {
						if (
							element.PermissionId ===
							item.permissions[i].permissionId
						) {
							let permissionAction = 'Unknown';
							let dots = item.permissions[i].permissionCode
								.trim()
								.split('.');
							if (dots.length > 1) {
								permissionAction = dots[dots.length - 1];
								permissionAction =
									permissionAction.charAt(0).toUpperCase() +
									permissionAction.slice(1);
							}
							newArr.push({
								groupName: item.groupName,
								permissionId: item.permissions[i].permissionId,
								permission: item.permissions[i].permissionName,
								permissionAction: permissionAction
							});
						}
					}
				});
			});
			return newArr;
		},
		groupByGroupName(arr) {
			const res = [];
			const groups = {};
			arr.forEach(item => {
				if (!groups[item.groupName]) {
					groups[item.groupName] = [];
				}
				groups[item.groupName].push(item.permissionAction);
			});
			Object.keys(groups).forEach(k => {
				let permissionNameProperCase =
					k.charAt(0).toUpperCase() + k.slice(1);
				res.push({
					permissionName: k,
					permissionNameFormatted: permissionNameProperCase,
					access: groups[k].join(', ')
				});
			});
			return res;
		},
		exportCSV(response) {
			let rawData = this.parseRolesHistoryLogs(response, true);
			const data = [['Date/Time', 'Action', 'Changed By']];
			rawData.items.forEach(change => {
				data.push([
					this.encodeCSV(
						`${change.timestamp}, ${change.differenceDateString}`
					),
					this.encodeCSV(change.action),
					this.encodeCSV(change.changedByUserName)
				]);
			});

			const csvContent = data.map(row => row.join(',')).join('\n');
			const csvFilename = 'role-history-log.csv';
			const blob = new Blob([csvContent], { type: 'text/csv' });

			const downloadLink = document.createElement('a');
			downloadLink.href = URL.createObjectURL(blob);
			downloadLink.download = csvFilename;
			downloadLink.click();
			URL.revokeObjectURL(downloadLink.href);
		},
		encodeCSV(value) {
			return value.includes(',') ? `"${value}"` : value;
		},
		pixelToCharacterLen(element) {
			const PIXEL_TO_CHARACTER = 0.125;
			return Math.ceil(element * PIXEL_TO_CHARACTER);
		}
	},
	watch: {
		windowWidth: {
			deep: true,
			handler() {
				if (this.$refs.actionWidthRef) {
					const newWidth = this.$refs.actionWidthRef.clientWidth;
					if (newWidth != this.actionClientWidth) {
						this.refreshSource(newWidth);
					}
				}
			}
		}
	}
}